import React from 'react';
import * as HereMapService from '../../services/here-maps/here-map-service';
import * as HereMapWrapper from '../../services/here-maps/here-map-wrapper';
import { HereMapContext } from './here-map-context';

enum HereMapMarkerTypes {
    MOBILE = 'mobile',
    SPEED = 'speed',
    TRAILER = 'trailer',
}

type HereMapMarkerType = `${HereMapMarkerTypes}`;
interface HereMapMarkerProps {
    latitude: number | string;
    longitude: number | string;
    icon?: string;
    onClick?: () => void;
    tooltipSticky?: boolean;
    visibility?: boolean;
    type?: HereMapMarkerType;
}

export const HereMapMarker = (props: React.PropsWithChildren<HereMapMarkerProps>) => {
    const { latitude, longitude, icon, onClick, children, tooltipSticky, visibility = true, type } = props;
    const [marker, setMarker] = React.useState<H.map.DomMarker | null>(null);
    const [bubble, setBubble] = React.useState<H.ui.InfoBubble | null>(null);
    const context = React.useContext(HereMapContext);

    // set up info marker and bubble
    React.useEffect(() => {
        if (!context.map) {
            return;
        }

        const newMarker = HereMapWrapper.createMarker(latitude, longitude, icon, visibility, type);
        context.map.addObject(newMarker);
        setMarker(newMarker);


        let infoBubble: H.ui.InfoBubble;
        if (children) {
            infoBubble = HereMapWrapper.createInfoBubble(latitude, longitude, children);
            setBubble(infoBubble);
            context.ui.addBubble(infoBubble);
        }

        return () => {
            context.map.removeObject(newMarker);
            if (infoBubble) {
                context.ui.removeBubble(infoBubble);
            }
        };
    }, [context.map]);


    React.useEffect(() => {
        if (marker) {
            const clickHandler = () => onClick?.();
            marker.addEventListener('tap', clickHandler);
            return () => {
                marker.removeEventListener('tap', clickHandler);
            }
        }
    }, [marker, onClick]);

    React.useEffect(() => {
        if (bubble && marker) {
            const unsubscribe = HereMapService.setTooltipBehavior2(bubble, tooltipSticky, marker);
            return unsubscribe;
        }
    }, [marker, bubble, tooltipSticky]);


    // update marker
    React.useEffect(() => {
        if (marker) {
            const position = { lat: Number(latitude), lng: Number(longitude) };

            marker.setGeometry(position);
            if (bubble) {
                bubble.setPosition(position);
            }
        }
    }, [marker, bubble, latitude, longitude]);


    // update info bubble
    React.useEffect(() => {
        if (bubble) {
            HereMapService.setInfoBubbleContent(bubble, children);
        }
    }, [marker, bubble, children, tooltipSticky]);


    React.useEffect(() => {
        if (marker && icon) {
            HereMapService.setMarkerIcon(marker, icon);
        }
    }, [marker, icon]);


    return null;
};
